import { Meteor } from 'meteor/meteor';
import { Mongo } from 'meteor/mongo';
import { DeclareMethod } from '/imports/methods';
import { ensureIndexes } from '/imports/api/ensureIndex';
import { Doc, ListMethodArg, ListMethodResponse, GetMethodResponse, ModelClass, References } from '/imports/types';
import type { PolicyRule } from '/imports/api/inventoryPolicyResolve';
// Resolution is Meteor-free for unit tests; re-exported so callers have one
// import for everything policy-related.
export { policyQtyFor, policyAdQtyFor, ruleToQuery, ruleMatches, type PolicyRule, type PolicyLike, type PolicyCard } from '/imports/api/inventoryPolicyResolve';

/**
 * Reusable Inventory Policies: named per-card stock QUANTITIES.
 *
 * A policy never changes which cards a bot stocks -- membership always comes
 * from the sell selection chain. It refines how many copies to hold: rules
 * (first-match-wins) map card/set/format/foil predicates to a stock target,
 * with an optional defaultQty for everything else. No policy, no matching
 * rule, or no defaultQty all fall through to the fleet/Settings sellCap and
 * then the bot's stocksize -- an unassigned policy system behaves exactly as
 * before.
 *
 * Referenced (not embedded) from `fleet.inventoryPolicyId` and
 * `bot.config.inventoryPolicyId` (bot wins), so editing a policy updates
 * every fleet and bot that uses it.
 */
export interface InventoryPolicy extends Doc {
  _id: string;
  /** Unique display name, e.g. 'New Set Deep Stock'. */
  name: string;
  description?: string;
  /** Stock TARGET when no rule matches. null/absent = fall through to sellCap/stocksize (default 8). */
  defaultQty?: number | null;
  /** ADVERTISED quantity (max offered for sale) when the matched rule has no
   *  adQty. Cannot exceed defaultQty. null/absent = fall through to the
   *  sellCap/shelfsize chain (default 4). */
  defaultAdQty?: number | null;
  /** First-match-wins, evaluated in listed order. */
  rules?: PolicyRule[];
}

type Model = InventoryPolicy;
type Refs = References<Model>;

export const InventoryPolicies = new Mongo.Collection<InventoryPolicy>('inventoryPolicies');

Meteor.isServer && (async () => {
  // Unique: two policies with one name is a config ambiguity, not a feature.
  await ensureIndexes(InventoryPolicies, [
    { keys: { name: 1 }, options: { unique: true } },
  ]);
})();

export const listInventoryPolicies = DeclareMethod<ListMethodArg<InventoryPolicy>, ListMethodResponse<InventoryPolicy, Refs>>('listInventoryPolicies');
export const getInventoryPolicy = DeclareMethod<string, GetMethodResponse<InventoryPolicy, Refs>>('getInventoryPolicy');
export const createInventoryPolicy = DeclareMethod<Partial<InventoryPolicy>, string>('createInventoryPolicy');
export const updateInventoryPolicy = DeclareMethod<Partial<InventoryPolicy>, string>('updateInventoryPolicy');
export const deleteInventoryPolicy = DeclareMethod<string, undefined>('deleteInventoryPolicy');

/** Everything that references a policy — shown on its page, and what makes a
 *  delete refusal explain itself. */
export const inventoryPolicyReferences = DeclareMethod<{ policyId: string }, {
  fleets: { _id: string; name: string }[];
  bots: { _id: string; username: string }[];
}>('inventoryPolicyReferences');

/** Per-rule EFFECTIVE hit counts (first-match-wins: rule N's count excludes
 *  cards already claimed by rules 0..N-1), plus the defaultQty remainder.
 *  Counts run against the whole card catalog -- membership is still governed
 *  by the sell selection, so they are indicative, not exact stock plans. */
export const inventoryPolicyRuleCounts = DeclareMethod<{ rules: PolicyRule[] }, {
  ok: boolean;
  error?: string;
  counts: number[];
}>('inventoryPolicyRuleCounts');

export const modelClass: ModelClass<Model, Refs> = {
  collection: InventoryPolicies,
  editableFields: ['name', 'description', 'defaultQty', 'defaultAdQty', 'rules'],
  searchQuery: (search?: string) => {
    search = search?.trim();
    return search?.length
      ? { $or: [{ name: new RegExp(search, 'i') }, { description: new RegExp(search, 'i') }] } as any
      : {};
  },
  listMethod: listInventoryPolicies,
  getMethod: getInventoryPolicy,
  createMethod: createInventoryPolicy,
  updateMethod: updateInventoryPolicy,
  deleteMethod: deleteInventoryPolicy,
  pathPrefix: '/inventory-policies',
  modelName: 'Inventory Policy',
  pluralName: 'Inventory Policies',
  icon: 'packageBox',
};
